import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpResponse } from '@angular/common/http';

import { promiser } from '@sg/utils';
import { SgAsyncGuiService, SgAsyncGuiOptions } from '@sg/ui-kit/async-gui';
import { SgAsyncGuiToasterOptions } from '../toaster-async-gui-plugin/src/lib/plugin';

interface StoredAsyncGuiInstruction {
  id: string;
  timestamp: number;
  options: SgAsyncGuiOptions;
}

const store = {
  items: [] as StoredAsyncGuiInstruction[],
  counter: 0,
  add(options: SgAsyncGuiOptions): StoredAsyncGuiInstruction {
    const instructions: StoredAsyncGuiInstruction = { id: String(this.counter++), timestamp: Date.now(), options };
    this.items.push(instructions);
    // TODO: use setTimeout to clear stale instructions.
    return instructions;
  },
  remove(id: number | string): StoredAsyncGuiInstruction | undefined {
    const idx = this.items.findIndex((item) => item.id === id);
    if (idx > -1) {
      return this.items.splice(idx, 1)[0];
    }
  },
};

export const AsyncGuiStyles = {
  toasterError(toasterOptions?: SgAsyncGuiToasterOptions) {
    const options: SgAsyncGuiOptions = { style: 'toaster', applyOn: ['reject'], toaster: { duration: 2000 } };
    if (toasterOptions) {
      Object.assign(options.toaster, toasterOptions);
    }
    return { sgAsyncGuiPlugin: store.add(options).id };
  },
  toasterSuccess(toasterOptions?: SgAsyncGuiToasterOptions) {
    const options: SgAsyncGuiOptions = {
      style: 'toaster',
      applyOn: ['resolve', 'reject'],
      toaster: { duration: 2000 },
    };
    if (toasterOptions) {
      Object.assign(options.toaster, toasterOptions);
    }
    return { sgAsyncGuiPlugin: store.add(options).id };
  },
  toasterWait(toasterOptions?: SgAsyncGuiToasterOptions) {
    const options: SgAsyncGuiOptions = {
      style: 'toaster',
      applyOn: ['run', 'resolve', 'reject'],
      toaster: { duration: 5000, singleToaster: true },
    };
    if (toasterOptions) {
      Object.assign(options.toaster, toasterOptions);
    }
    return { sgAsyncGuiPlugin: store.add(options).id };
  },
  dialogWait(dialogOptions?: SgAsyncGuiOptions) {
    const options: SgAsyncGuiOptions = { style: 'dialog', applyOn: ['run', 'resolve', 'reject'] };
    if (dialogOptions) {
      Object.assign(options, dialogOptions);
    }
    return { sgAsyncGuiPlugin: store.add(options).id };
  },
};

@Injectable()
export class SgAsyncGuiHttpInterceptor implements HttpInterceptor {
  constructor(private asyncGui: SgAsyncGuiService) {}
  intercept(req: HttpRequest<any>, next: HttpHandler) {
    if (req.headers.has('sgAsyncGuiPlugin')) {
      const id = req.headers.get('sgAsyncGuiPlugin');
      req = req.clone({ headers: req.headers.delete('sgAsyncGuiPlugin') });

      const instructions = store.remove(id);
      if (instructions) {
        const p = promiser();
        this.asyncGui.open(p.promise, instructions.options);
        return next.handle(req).pipe(
          tap(
            (event) => {
              event instanceof HttpResponse && p.resolve(event);
            },
            (err) => {
              p.reject(err);
            }
          )
        );
      }
    }
    return next.handle(req);
  }
}
